import { ContainerData, ModuleContent, ModuleData, ModuleClickAction, ContainerStyle, PlaceholderData } from './contract';
import { bizLinkLegacy, convertLegacyLink } from '../bizLink';
import { getPathWithRootPath } from '../../Services/common';
import { mapFromLegacyPermissions, PermissionLegacy } from '../permission';

// ---------------------------------------------------------------------------
// Legacy API response types
// ---------------------------------------------------------------------------

/**
 * Shape of the container object as returned by the legacy backend API.
 * These types mirror the backend C# entities serialized to JSON.
 */
export type LegacyApiContainer = {
	Id: number;
	Style?: string | Record<string, unknown>;
	MobileStyle?: string | Record<string, unknown>;
	PlaceholderMode?: string;
	Placeholders: LegacyApiPlaceholder[];
};

export type LegacyApiPlaceholder = {
	Id: number;
	Name: string;
	Type?: number;
	Modules: LegacyApiModule[];
	Permissions: PermissionLegacy[];
};

export type LegacyApiModule = {
	Id: number;
	Guid: string;
	/** SystemName from ModuleAbstract – acts as the module type key. */
	SystemName?: string;
	/** Display name from ModuleAbstract. */
	Name?: string;
	/** JSON string or already-parsed object containing module-specific data. */
	Content: string | Record<string, unknown>;
	/** JSON string or already-parsed object containing styling data. */
	Style?: string | Record<string, unknown>;
	MobileStyle?: string | Record<string, unknown>;
	CssClasses?: string | string[];
	OnClick?: string | Record<string, unknown>;
	Order: number;
	/** Nested child modules (e.g. inside a Segment). */
	Modules?: LegacyApiModule[];
	UseSettings?: boolean;
	Optional?: boolean;
	HasFinishedBefore?: boolean;
};

type LegacyApiContainerCamelCase = {
	id: number;
	style?: string | Record<string, unknown>;
	mobileStyle?: string | Record<string, unknown>;
	placeholderMode?: string;
	placeholders: LegacyApiPlaceholderCamelCase[];
};

type LegacyApiPlaceholderCamelCase = {
	id: number;
	name: string;
	type?: number;
	modules: LegacyApiModuleCamelCase[];
	permissions: PermissionLegacy[];
};

type LegacyApiModuleCamelCase = {
	id: number;
	guid: string;
	systemName?: string;
	name?: string;
	content: string | Record<string, unknown>;
	style?: string | Record<string, unknown>;
	mobileStyle?: string | Record<string, unknown>;
	cssClasses?: string | string[];
	onClick?: string | Record<string, unknown>;
	order: number;
	modules?: LegacyApiModuleCamelCase[];
	useSettings?: boolean;
	optional?: boolean;
	hasFinishedBefore?: boolean;
};

export type LegacyApiContainerInput = LegacyApiContainer | LegacyApiContainerCamelCase;

// ---------------------------------------------------------------------------
// Normalizer functions
// ---------------------------------------------------------------------------

/**
 * Converts a legacy API container response into the new ContainerData contract.
 */
export function normalizeContainer(legacy: LegacyApiContainerInput | ContainerData): ContainerData {
	if (isContainerData(legacy)) {
		return legacy;
	}

	const parsedRawStyle = extractRawStyle(getLegacyContainerStyle(legacy));
	const rawMobileStyle = extractRawStyle(getLegacyContainerMobileStyle(legacy)) ?? asRecord(parsedRawStyle?.Mobile);
	const placeholders = getLegacyContainerPlaceholders(legacy).map(normalizePlaceholder);
	return {
		id: getLegacyContainerId(legacy),
		placeholders,
		style: parseStyle(parsedRawStyle),
		mobileStyle: parseStyle(rawMobileStyle),
		placeholderMode: getLegacyContainerPlaceholderMode(legacy) === 'composed' ? 'composed' : 'variant',
	};
}

export function isContainerData(value: unknown): value is ContainerData {
	const container = asRecord(value);
	if (!container) {
		return false;
	}

	if (container.placeholderMode !== 'composed' && container.placeholderMode !== 'variant') {
		return false;
	}

	if (!Array.isArray(container.placeholders)) {
		return false;
	}

	return container.placeholders.every((placeholder) => {
		const placeholderRecord = asRecord(placeholder);
		return (
			placeholderRecord != null &&
			typeof placeholderRecord.id === 'number' &&
			typeof placeholderRecord.name === 'string' &&
			Array.isArray(placeholderRecord.modules) &&
			placeholderRecord.modules.every(isModuleData)
		);
	});
}

function isModuleData(value: unknown): value is ModuleData {
	const module = asRecord(value);
	if (!module) {
		return false;
	}

	return (
		typeof module.id === 'number' &&
		typeof module.guid === 'string' &&
		typeof module.moduleType === 'string' &&
		Array.isArray(module.children) &&
		module.children.every(isModuleData)
	);
}

function normalizePlaceholder(legacy: LegacyApiPlaceholder | LegacyApiPlaceholderCamelCase): PlaceholderData {
	return {
		id: getLegacyPlaceholderId(legacy),
		name: getLegacyPlaceholderName(legacy),
		modules: getLegacyPlaceholderModules(legacy)
			.sort((a, b) => getLegacyModuleOrder(a) - getLegacyModuleOrder(b))
			.map(normalizeModule),
		permissions: mapFromLegacyPermissions(getLegacyPlaceholderPermissions(legacy)),
	};
}

function normalizeModule(legacy: LegacyApiModule | LegacyApiModuleCamelCase): ModuleData {
	const parsedRawStyle = extractRawStyle(getLegacyModuleStyle(legacy));
	const rawMobileStyle = extractRawStyle(getLegacyModuleMobileStyle(legacy)) ?? asRecord(parsedRawStyle?.Mobile);
	const moduleType = getLegacyModuleType(legacy);
	const rawContentSource = getLegacyModuleContent(legacy);

	// Parse raw content first to extract cross-cutting properties
	const rawContent: Record<string, unknown> =
		rawContentSource == null ? {} : typeof rawContentSource === 'string' ? safeParse(rawContentSource) : rawContentSource;

	const cssClasses = getLegacyModuleCssClasses(legacy) ?? parseLegacyCssClasses(rawContent);

	// Extract click action — only one of link / expand / scroll can be active
	const onClick =
		parseStoredClickAction(getLegacyModuleOnClick(legacy)) ??
		extractClickAction(rawContent, moduleType, getLegacyModuleId(legacy));

	const style = parseStyle(parsedRawStyle);
	if (onClick?.type === 'link') {
		const linkConfig = asRecord(rawContent.ContainerModuleLink);
		if (linkConfig) {
			style.hover = {
				color: getFirstString(linkConfig, ['HoverColor']) || undefined,
				decoration: getFirstString(linkConfig, ['HoverDecoration']) as
					| 'underline'
					| 'line-through'
					| 'overline'
					| 'none'
					| undefined,
			};
		}
	}

	const module: ModuleData = {
		id: getLegacyModuleId(legacy),
		guid: getLegacyModuleGuid(legacy),
		moduleType: moduleType ?? 'Unknown',
		order: getLegacyModuleOrder(legacy),
		content: parseContent(rawContentSource, moduleType),
		style,
		mobileStyle: parseStyle(rawMobileStyle),
		cssClasses,
		children: getLegacyModuleChildren(legacy)
			.sort((a, b) => getLegacyModuleOrder(a) - getLegacyModuleOrder(b))
			.map(normalizeModule),
		onClick,
		isFinishable: getLegacyModuleUseSettings(legacy),
		isOptional: getLegacyModuleOptional(legacy),
		userStatus: {
			hasFinishedBefore: getLegacyModuleHasFinishedBefore(legacy),
		},
	};

	return module;
}

/**
 * Parses the Content field which may be a JSON string or an already-parsed object.
 * Applies module-type-specific normalization when needed.
 */
function parseContent(raw: string | Record<string, unknown> | null | undefined, moduleType: string | undefined): ModuleContent {
	if (raw == null) return {};

	const parsed: Record<string, unknown> = typeof raw === 'string' ? safeParse(raw) : raw;

	switch (moduleType) {
		case 'Text':
			return normalizeTextContent(parsed);
		case 'Button':
			return normalizeButtonContent(parsed);
		case 'ConfirmRead':
			return normalizeConfirmReadContent(parsed);
		case 'CookieButton':
			return normalizeCookieButtonContent(parsed);
		case 'WelcomeGuideButton':
			return normalizeWelcomeGuideButtonContent(parsed);
		case 'Image':
			return normalizeImageContent(parsed);
		case 'AnimatedSvg':
			return normalizeAnimatedSvgContent(parsed);
		case 'BizAgent':
			return normalizeBizAgentContent(parsed);
		case 'Video':
			return normalizeVideoContent(parsed);
		case 'IFrame':
			return normalizeIFrameContent(parsed);
		case 'Segment':
			return normalizeSegmentContent(parsed);
		case 'Form':
			return normalizeFormContent(parsed);
		default:
			return parsed;
	}
}

function getLegacyContainerId(legacy: LegacyApiContainerInput): number {
	return isCamelCaseContainer(legacy) ? legacy.id : legacy.Id;
}

function getLegacyContainerStyle(legacy: LegacyApiContainerInput): string | Record<string, unknown> | undefined {
	return isCamelCaseContainer(legacy) ? legacy.style : legacy.Style;
}

function getLegacyContainerMobileStyle(legacy: LegacyApiContainerInput): string | Record<string, unknown> | undefined {
	return isCamelCaseContainer(legacy) ? legacy.mobileStyle : legacy.MobileStyle;
}

function getLegacyContainerPlaceholderMode(legacy: LegacyApiContainerInput): string | undefined {
	return isCamelCaseContainer(legacy) ? legacy.placeholderMode : legacy.PlaceholderMode;
}

function getLegacyContainerPlaceholders(
	legacy: LegacyApiContainerInput
): Array<LegacyApiPlaceholder | LegacyApiPlaceholderCamelCase> {
	return isCamelCaseContainer(legacy) ? legacy.placeholders : legacy.Placeholders;
}

function getLegacyPlaceholderId(legacy: LegacyApiPlaceholder | LegacyApiPlaceholderCamelCase): number {
	return isCamelCasePlaceholder(legacy) ? legacy.id : legacy.Id;
}

function getLegacyPlaceholderName(legacy: LegacyApiPlaceholder | LegacyApiPlaceholderCamelCase): string {
	return isCamelCasePlaceholder(legacy) ? legacy.name : legacy.Name;
}

function getLegacyPlaceholderModules(
	legacy: LegacyApiPlaceholder | LegacyApiPlaceholderCamelCase
): Array<LegacyApiModule | LegacyApiModuleCamelCase> {
	return isCamelCasePlaceholder(legacy) ? legacy.modules : legacy.Modules;
}

function getLegacyPlaceholderPermissions(legacy: LegacyApiPlaceholder | LegacyApiPlaceholderCamelCase): PermissionLegacy[] {
	return isCamelCasePlaceholder(legacy) ? legacy.permissions : legacy.Permissions;
}

function getLegacyModuleId(legacy: LegacyApiModule | LegacyApiModuleCamelCase): number {
	return isCamelCaseModule(legacy) ? legacy.id : legacy.Id;
}

function getLegacyModuleGuid(legacy: LegacyApiModule | LegacyApiModuleCamelCase): string {
	return isCamelCaseModule(legacy) ? legacy.guid : legacy.Guid;
}

function getLegacyModuleType(legacy: LegacyApiModule | LegacyApiModuleCamelCase): string | undefined {
	if (!isCamelCaseModule(legacy)) {
		return legacy.SystemName ?? legacy.Name;
	}

	return legacy.systemName ?? legacy.name;
}

function getLegacyModuleContent(legacy: LegacyApiModule | LegacyApiModuleCamelCase): string | Record<string, unknown> {
	return isCamelCaseModule(legacy) ? legacy.content : legacy.Content;
}

function getLegacyModuleStyle(legacy: LegacyApiModule | LegacyApiModuleCamelCase): string | Record<string, unknown> | undefined {
	return isCamelCaseModule(legacy) ? legacy.style : legacy.Style;
}

function getLegacyModuleMobileStyle(
	legacy: LegacyApiModule | LegacyApiModuleCamelCase
): string | Record<string, unknown> | undefined {
	return isCamelCaseModule(legacy) ? legacy.mobileStyle : legacy.MobileStyle;
}

function getLegacyModuleCssClasses(legacy: LegacyApiModule | LegacyApiModuleCamelCase): string[] | undefined {
	return isCamelCaseModule(legacy) ? parseStringArray(legacy.cssClasses) : parseStringArray(legacy.CssClasses);
}

function getLegacyModuleOnClick(
	legacy: LegacyApiModule | LegacyApiModuleCamelCase
): string | Record<string, unknown> | undefined {
	return isCamelCaseModule(legacy) ? legacy.onClick : legacy.OnClick;
}

function getLegacyModuleOrder(legacy: LegacyApiModule | LegacyApiModuleCamelCase): number {
	return isCamelCaseModule(legacy) ? legacy.order : legacy.Order;
}

function getLegacyModuleChildren(
	legacy: LegacyApiModule | LegacyApiModuleCamelCase
): Array<LegacyApiModule | LegacyApiModuleCamelCase> {
	return isCamelCaseModule(legacy) ? (legacy.modules ?? []) : (legacy.Modules ?? []);
}

function getLegacyModuleUseSettings(legacy: LegacyApiModule | LegacyApiModuleCamelCase): boolean {
	return isCamelCaseModule(legacy) ? (legacy.useSettings ?? false) : (legacy.UseSettings ?? false);
}

function getLegacyModuleOptional(legacy: LegacyApiModule | LegacyApiModuleCamelCase): boolean {
	return isCamelCaseModule(legacy) ? (legacy.optional ?? false) : (legacy.Optional ?? false);
}

function getLegacyModuleHasFinishedBefore(legacy: LegacyApiModule | LegacyApiModuleCamelCase): boolean {
	return isCamelCaseModule(legacy) ? (legacy.hasFinishedBefore ?? false) : (legacy.HasFinishedBefore ?? false);
}

function isCamelCaseContainer(legacy: LegacyApiContainerInput): legacy is LegacyApiContainerCamelCase {
	return 'id' in legacy;
}

function isCamelCasePlaceholder(
	legacy: LegacyApiPlaceholder | LegacyApiPlaceholderCamelCase
): legacy is LegacyApiPlaceholderCamelCase {
	return 'id' in legacy;
}

function isCamelCaseModule(legacy: LegacyApiModule | LegacyApiModuleCamelCase): legacy is LegacyApiModuleCamelCase {
	return 'id' in legacy;
}

/**
 * Extracts the raw style object from a legacy Style field (JSON string or object).
 * Returns the parsed record so callers can access sub-properties like `Mobile`
 * before passing into `parseStyle`.
 */
function extractRawStyle(raw: string | Record<string, unknown> | null | undefined): Record<string, unknown> | undefined {
	if (raw == null) return undefined;
	return typeof raw === 'string' ? safeParse(raw) : raw;
}

/**
 * Extracts a single click action from the legacy content.
 * Only one of link / expand-segment / scroll-to-segment can be active.
 */
function extractClickAction(
	rawContent: Record<string, unknown>,
	moduleType?: string,
	moduleId?: number
): ModuleClickAction | undefined {
	if (moduleType === 'Button') {
		const buttonClickAction = extractButtonClickAction(rawContent);
		if (buttonClickAction) {
			return buttonClickAction;
		}
	}

	if (moduleType === 'ConfirmRead' && moduleId !== undefined) {
		const confirmReadClickAction = extractConfirmReadClickAction(rawContent, moduleId);
		if (confirmReadClickAction) {
			return confirmReadClickAction;
		}
	}

	// Link takes priority, then expand, then scroll (matching legacy mutual-exclusion)
	const linkConfig = asRecord(rawContent.ContainerModuleLink);
	if (linkConfig && linkConfig.Enabled === true && linkConfig.UrlData) {
		const link = convertLegacyLink(linkConfig.UrlData as bizLinkLegacy);
		if (linkConfig.NewTab === true) {
			link.target = '_blank';
		}
		return { type: 'link', link };
	}

	const expandConfig = asRecord(rawContent.ContainerModuleExpandSegment);
	if (expandConfig && expandConfig.Enabled === true) {
		const guid = getFirstString(expandConfig as Record<string, unknown>, ['Guid']);
		if (guid) return { type: 'expand-segment', segmentGuid: guid };
	}

	const scrollConfig = asRecord(rawContent.ContainerModuleScroll);
	if (scrollConfig && scrollConfig.Enabled === true) {
		const guid = getFirstString(scrollConfig as Record<string, unknown>, ['Guid']);
		if (guid) return { type: 'scroll-to-segment', segmentGuid: guid };
	}

	return undefined;
}

function parseStoredClickAction(raw: string | Record<string, unknown> | undefined): ModuleClickAction | undefined {
	const parsed = typeof raw === 'string' ? safeParseUnknown(raw) : raw;
	const record = asRecord(parsed);
	if (!record) {
		return undefined;
	}

	const type = getFirstString(record, ['type']);
	if (type === 'link') {
		const linkValue = record.link;
		return linkValue && typeof linkValue === 'object'
			? { type: 'link', link: linkValue as never, useSelectedValues: record.useSelectedValues === true }
			: undefined;
	}
	if (type === 'select-value') {
		return { type, group: getFirstString(record, ['group']) ?? '', value: getFirstString(record, ['value']) ?? '' };
	}

	if (type === 'dispatch-events') {
		const eventNames = parseStringArray(record.eventNames);
		return eventNames.length > 0 ? { type: 'dispatch-events', eventNames } : undefined;
	}

	if (type === 'finish-module') {
		const moduleId = getFirstNumber(record, ['moduleId']);
		return moduleId != null ? { type: 'finish-module', moduleId } : undefined;
	}

	if (type === 'expand-segment' || type === 'scroll-to-segment') {
		const segmentGuid = getFirstString(record, ['segmentGuid']);
		return segmentGuid ? { type, segmentGuid } : undefined;
	}

	return undefined;
}

function extractConfirmReadClickAction(rawContent: Record<string, unknown>, moduleId: number): ModuleClickAction | undefined {
	const moduleType = getFirstString(rawContent, ['ModuleType', 'moduleType']) ?? 'Checkbox';

	if (moduleType !== 'Button') {
		return undefined;
	}

	return {
		type: 'finish-module',
		moduleId,
	};
}

function extractButtonClickAction(rawContent: Record<string, unknown>): ModuleClickAction | undefined {
	if (getFirstString(rawContent, ['ButtonType', 'buttonType']) === 'SelectValue') {
		return {
			type: 'select-value',
			group: getFirstString(rawContent, ['selectionGroup']) ?? '',
			value: getFirstString(rawContent, ['buttonLink', 'ButtonLink']) ?? '',
		};
	}
	const buttonType = getFirstString(rawContent, ['ButtonType', 'buttonType']) ?? 'Website';
	const buttonLink = getFirstString(rawContent, ['ButtonLink', 'buttonLink'])?.trim() ?? '';
	const openLinkNew = getFirstBoolean(rawContent, ['OpenLinkNew', 'openLinkNew']) ?? false;

	if (!buttonLink) {
		return undefined;
	}

	switch (buttonType) {
		case 'Phone':
			return {
				type: 'link',
				link: {
					type: 'url',
					href: `tel:${buttonLink}`,
				},
			};
		case 'Email':
			return {
				type: 'link',
				link: {
					type: 'url',
					href: `mailto:${buttonLink}`,
				},
			};
		case 'Broadcast': {
			const eventNames = buttonLink
				.split(',')
				.map((eventName) => eventName.trim())
				.filter((eventName) => eventName.length > 0);

			return eventNames.length > 0 ? { type: 'dispatch-events', eventNames } : undefined;
		}
		case 'Website':
		default:
			return {
				type: 'link',
				link: {
					type: 'url',
					href: getPathWithRootPath(buttonLink),
					target: openLinkNew ? '_blank' : undefined,
				},
				useSelectedValues: getFirstBoolean(rawContent, ['useSelectedValues']) === true,
			};
	}
}

/**
 * Parses the Style field and maps legacy PascalCase property names to camelCase.
 */
function parseStyle(raw: Record<string, unknown> | null | undefined): ContainerStyle {
	if (raw == null) return {};

	const parsed = raw;

	const paddingTop = getFirstString(parsed, ['PaddingTop', 'paddingTop']);
	const paddingRight = getFirstString(parsed, ['PaddingRight', 'paddingRight']);
	const paddingBottom = getFirstString(parsed, ['PaddingBottom', 'paddingBottom']);
	const paddingLeft = getFirstString(parsed, ['PaddingLeft', 'paddingLeft']);
	const marginTop = getFirstString(parsed, ['MarginTop', 'marginTop']);
	const marginRight = getFirstString(parsed, ['MarginRight', 'marginRight']);
	const marginBottom = getFirstString(parsed, ['MarginBottom', 'marginBottom']);
	const marginLeft = getFirstString(parsed, ['MarginLeft', 'marginLeft']);

	const width = getFirstString(parsed, ['Width', 'width']);
	const maxWidth = getFirstString(parsed, ['MaxWidth', 'maxWidth']);
	const minWidth = getFirstString(parsed, ['MinWidth', 'minWidth']);
	const minHeight = getFirstString(parsed, ['MinHeight', 'minHeight']);
	const maxHeight = getFirstString(parsed, ['MaxHeight', 'maxHeight']);

	const textColor = getFirstString(parsed, ['TextColor', 'textColor']);
	const textSize = getFirstString(parsed, ['TextSize', 'textSize']);
	const textLineHeight = getFirstString(parsed, ['LineHeight', 'lineHeight']);
	const textAlign = getFirstString(parsed, ['TextAlign', 'textAlign']);
	const textItalic = getFirstBoolean(parsed, ['Italic', 'italic']);
	const textBold = getFirstBoolean(parsed, ['Bold', 'bold']);
	const textFontFamily = getFirstString(parsed, ['FontFamily', 'fontFamily']);

	const borderWidth = getFirstString(parsed, ['BorderWidth', 'borderWidth']);
	const borderColor = getFirstString(parsed, ['BorderColor', 'borderColor']);
	const borderStyle = getFirstString(parsed, ['BorderStyle', 'borderStyle']);
	const borderRadius = getFirstString(parsed, ['BorderRadius', 'borderRadius']);

	const background = asRecord(parsed.background) ?? {};
	const backgroundSize =
		getFirstString(parsed, ['BackgroundImageSize', 'backgroundImageSize']) ?? getFirstString(background, ['size']);
	const backgroundZoom =
		getFirstNumber(parsed, ['BackgroundImageZoom', 'backgroundImageZoom']) ?? getFirstNumber(background, ['zoom']);
	const backgroundImageUrl =
		getFirstString(parsed, ['BackgroundImage', 'backgroundImage']) ?? getFirstString(background, ['imageUrl']);
	const backgroundPositionX =
		getFirstString(parsed, ['BackgroundImageFocusX', 'backgroundImageFocusX']) ?? getFirstString(background, ['positionX']);
	const backgroundPositionY =
		getFirstString(parsed, ['BackgroundImageFocusY', 'backgroundImageFocusY']) ?? getFirstString(background, ['positionY']);
	const backgroundBrightness =
		getFirstNumber(parsed, ['BackgroundImageBrightness', 'backgroundImageBrightness']) ??
		getFirstNumber(background, ['brightness']);
	const backgroundBlur =
		getFirstNumber(parsed, ['BackgroundImageBlur', 'backgroundImageBlur']) ?? getFirstNumber(background, ['blur']);
	const backgroundOpacity =
		getFirstNumber(parsed, ['BackgroundImageOpacity', 'backgroundImageOpacity']) ?? getFirstNumber(background, ['opacity']);
	const backgroundColor =
		getFirstString(parsed, ['BackgroundColor', 'backgroundColor']) ?? getFirstString(background, ['color']);

	const effects = parseEffects(asRecord(parsed.Effects) ?? asRecord(parsed.effects));

	const hiddenOnDesktop = getFirstBoolean(parsed, ['HideOnDesktop', 'hiddenOnDesktop']);
	const hiddenOnMobile = getFirstBoolean(parsed, ['HideOnMobile', 'hiddenOnMobile']);

	const getSpacing = (top?: string, right?: string, bottom?: string, left?: string) => {
		if (top || right || bottom || left) {
			return `${normalizeUnit(top) ?? 0} ${normalizeUnit(right) ?? 0} ${normalizeUnit(bottom) ?? 0} ${normalizeUnit(left) ?? 0}`;
		}
		return undefined;
	};

	return {
		desktopWidthClass: getFirstString(parsed, ['DesktopWidthClass', 'desktopWidthClass']),
		mobileWidthClass: getFirstString(parsed, ['MobileWidthClass', 'mobileWidthClass']),
		hidden: hiddenOnDesktop && hiddenOnMobile ? 'both' : hiddenOnDesktop ? 'desktop' : hiddenOnMobile ? 'mobile' : undefined,
		spacing: {
			padding: getSpacing(paddingTop, paddingRight, paddingBottom, paddingLeft),
			margin: getSpacing(marginTop, marginRight, marginBottom, marginLeft),
		},
		size: {
			width,
			maxWidth,
			minWidth,
			minHeight,
			maxHeight,
		},
		text: {
			color: textColor,
			size: textSize,
			lineHeight: textLineHeight,
			align: textAlign === 'left' || textAlign === 'center' || textAlign === 'right' ? textAlign : undefined,
			style: textItalic ? 'italic' : undefined,
			weight: textBold ? 'bold' : undefined,
			fontFamily: textFontFamily,
		},
		border: {
			width: normalizeUnit(borderWidth),
			color: borderColor,
			style: borderStyle,
			radius: normalizeUnit(borderRadius),
		},
		background: {
			imageUrl: backgroundImageUrl,
			positionX: backgroundPositionX,
			positionY: backgroundPositionY,
			size: backgroundSize === 'cover' || backgroundSize === 'contain' ? backgroundSize : undefined,
			zoom: backgroundZoom,
			brightness: backgroundBrightness,
			blur: backgroundBlur,
			opacity: backgroundOpacity,
			color: backgroundColor,
		},
		effects,
	};
}

function parseEffects(raw: Record<string, unknown> | undefined): ContainerStyle['effects'] {
	if (!raw) return undefined;

	const reveal = asRecord(raw.Reveal) ?? asRecord(raw.reveal);
	const revealType = reveal ? getFirstString(reveal, ['Type', 'type']) : undefined;
	const revealDirection = reveal ? getFirstString(reveal, ['Direction', 'direction']) : undefined;

	const hover = asRecord(raw.Hover) ?? asRecord(raw.hover);
	const hoverMove = hover ? (asRecord(hover.Move) ?? asRecord(hover.move)) : undefined;
	const hoverMoveDirection = hoverMove ? getFirstString(hoverMove, ['Direction', 'direction']) : undefined;

	return {
		reveal: reveal
			? {
					type: revealType === 'fade' || revealType === 'move' || revealType === 'scale' ? revealType : undefined,
					direction:
						revealDirection === 'up' ||
						revealDirection === 'right' ||
						revealDirection === 'down' ||
						revealDirection === 'left'
							? revealDirection
							: undefined,
					distance: getFirstString(reveal, ['Distance', 'distance']),
					scale: getFirstNumber(reveal, ['Scale', 'scale']),
					duration: getFirstString(reveal, ['Duration', 'duration']),
					delay: getFirstString(reveal, ['Delay', 'delay']),
				}
			: undefined,
		hover: hover
			? {
					move: hoverMove
						? {
								direction:
									hoverMoveDirection === 'up' ||
									hoverMoveDirection === 'right' ||
									hoverMoveDirection === 'down' ||
									hoverMoveDirection === 'left'
										? hoverMoveDirection
										: undefined,
								distance: getFirstString(hoverMove, ['Distance', 'distance']),
							}
						: undefined,
					shadow: getFirstBoolean(hover, ['Shadow', 'shadow']),
					scale: getFirstNumber(hover, ['Scale', 'scale']),
					textColor: getFirstString(hover, ['TextColor', 'textColor']),
					duration: getFirstString(hover, ['Duration', 'duration']),
				}
			: undefined,
	};
}

function normalizeUnit(value: string | undefined): string | undefined {
	if (value == null) return undefined;
	return /^\d+$/.test(value) ? `${value}px` : value;
}

function parseLegacyCssClasses(rawContent: Record<string, unknown>): string[] {
	return (
		getFirstString(rawContent, ['CssClass', 'cssClasses'])
			?.split(' ')
			.filter((cls) => cls) ?? []
	);
}

function parseStringArray(value: unknown): string[] {
	if (Array.isArray(value)) {
		return value.filter((item): item is string => typeof item === 'string' && item.length > 0);
	}

	if (typeof value === 'string') {
		const parsed = safeParseUnknown(value);
		if (Array.isArray(parsed)) {
			return parsed.filter((item): item is string => typeof item === 'string' && item.length > 0);
		}

		return value
			.split(' ')
			.map((item) => item.trim())
			.filter((item) => item.length > 0);
	}

	return [];
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
	if (value != null && typeof value === 'object' && !Array.isArray(value)) {
		return value as Record<string, unknown>;
	}
	return undefined;
}

function safeParseUnknown(json: string): unknown {
	try {
		return JSON.parse(json);
	} catch {
		return undefined;
	}
}

function safeParse(json: string): Record<string, unknown> {
	try {
		const result = safeParseUnknown(json);
		if (result !== null && typeof result === 'object' && !Array.isArray(result)) {
			return result as Record<string, unknown>;
		}
		return {};
	} catch {
		return {};
	}
}

// ---------------------------------------------------------------------------
// Module-specific content normalizers
// ---------------------------------------------------------------------------

function normalizeTextContent(raw: Record<string, unknown>): ModuleContent {
	return {
		text: getFirstString(raw, ['Text', 'text']) ?? '',
	};
}

function normalizeButtonContent(raw: Record<string, unknown>): ModuleContent {
	const buttonType = getFirstString(raw, ['ButtonType', 'buttonType']) ?? 'Website';

	return {
		buttonText: getFirstString(raw, ['ButtonText', 'buttonText']) ?? 'Allow all',
		buttonLink: getFirstString(raw, ['ButtonLink', 'buttonLink']) ?? '',
		buttonType:
			buttonType === 'Phone' || buttonType === 'Email' || buttonType === 'Broadcast' || buttonType === 'SelectValue'
				? buttonType
				: 'Website',
		selectionGroup: getFirstString(raw, ['selectionGroup']),
		useSelectedValues: getFirstBoolean(raw, ['useSelectedValues']) === true,
		openLinkNew: getFirstBoolean(raw, ['OpenLinkNew', 'openLinkNew']) ?? false,
	};
}

function normalizeConfirmReadContent(raw: Record<string, unknown>): ModuleContent {
	const moduleType = getFirstString(raw, ['ModuleType', 'moduleType']) ?? 'Checkbox';

	return {
		moduleType: moduleType === 'Button' ? 'Button' : 'Checkbox',
		checkboxLabel: getFirstString(raw, ['CheckboxLabel', 'checkboxLabel']) ?? '',
		buttonLabel: getFirstString(raw, ['ButtonLabel', 'buttonLabel']) ?? '',
		goToNext: getFirstBoolean(raw, ['GoToNext', 'goToNext']) ?? false,
	};
}

function normalizeCookieButtonContent(raw: Record<string, unknown>): ModuleContent {
	return {
		buttonText: getFirstString(raw, ['ButtonText', 'buttonText']) ?? 'Button',
		isRejectButton: getFirstBoolean(raw, ['IsRejectButton', 'isRejectButton']) ?? false,
	};
}

function normalizeWelcomeGuideButtonContent(raw: Record<string, unknown>): ModuleContent {
	return {
		buttonText: getFirstString(raw, ['ButtonText', 'buttonText']) ?? 'Lets get started!',
		buttonLink: getFirstString(raw, ['ButtonLink', 'buttonLink']) ?? '',
	};
}

function normalizeImageContent(raw: Record<string, unknown>): ModuleContent {
	return {
		src: getFirstString(raw, ['Src', 'src', 'Image', 'ImageUrl']) ?? '',
		alt: getFirstString(raw, ['Alt', 'alt', 'ImageAlt', 'imageAlt']),
	};
}

function normalizeAnimatedSvgContent(raw: Record<string, unknown>): ModuleContent {
	const animationBehavior = getFirstString(raw, ['AnimationBehavior', 'animationBehavior']);

	return {
		svgMarkup: getFirstString(raw, ['SvgMarkup', 'svgMarkup', 'Markup', 'markup']) ?? '',
		ariaLabel: getFirstString(raw, ['AriaLabel', 'ariaLabel', 'Alt', 'alt', 'AltText', 'altText']),
		caption: getFirstString(raw, ['Caption', 'caption']),
		animationBehavior:
			animationBehavior === 'playWhenVisible' || animationBehavior === 'disabled' ? animationBehavior : 'default',
	};
}

function normalizeBizAgentContent(raw: Record<string, unknown>): ModuleContent {
	return {
		configSystemName: getFirstString(raw, ['ConfigSystemName', 'configSystemName']) ?? '',
	};
}

function normalizeSegmentContent(raw: Record<string, unknown>): ModuleContent {
	if (typeof raw.expandable === 'object') return raw;
	const expanding = getFirstBoolean(raw, ['Expanding', 'expandable']);
	const startExpanded = getFirstBoolean(raw, ['StartExpanded', 'startExpanded']);
	const completelyHiddenWhenNotExpanded = getFirstBoolean(raw, [
		'CompletelyHiddenWhenNotExpanded',
		'completelyHiddenWhenNotExpanded',
	]);
	const expandPreviewAsButton = getFirstBoolean(raw, ['ExpandPreviewAsButton', 'expandPreviewAsButton']);
	const previewImage = getFirstString(raw, ['Image', 'previewImage']);
	const previewText = getFirstString(raw, ['PreviewText', 'previewText']);
	const name = getFirstString(raw, ['Name', 'name']);
	const columns = getFirstNumber(raw, ['Columns', 'columns']);
	const mobileColumns = getFirstNumber(raw, ['MobileColumns', 'mobileColumns']);
	const gap = getFirstString(raw, ['Gap', 'gap']);
	const mobileGap = getFirstString(raw, ['MobileGap', 'mobileGap']);

	const justifyContentLegacy = getFirstString(raw, ['JustifyContent', 'justifyContent']);
	const alignItemsLegacy = getFirstString(raw, ['HorizontalAlign', 'alignItems']); //Legacy used HorizontalAlign for alignItems

	// Legacy stores preview image as an object with FileUrl
	let previewImageUrl = previewImage;
	if (!previewImageUrl) {
		const imageObj = asRecord(raw.Image);
		if (imageObj) {
			previewImageUrl = getFirstString(imageObj as Record<string, unknown>, ['FileUrl', 'fileUrl']);
		}
	}

	return {
		name,
		expandable: {
			enabled: expanding ?? false,
			expandedByDefault: startExpanded ?? false,
			completelyHidden: completelyHiddenWhenNotExpanded ?? false,
			previewAsButton: expandPreviewAsButton ?? false,
			previewImageUrl,
			previewText,
		},
		columns: typeof columns === 'number' && columns >= 2 ? Math.min(Math.floor(columns), 6) : undefined,
		mobileColumns:
			typeof mobileColumns === 'number' && mobileColumns >= 1 ? Math.min(Math.floor(mobileColumns), 3) : undefined,
		gap: normalizeUnit(gap),
		mobileGap: normalizeUnit(mobileGap),
		justifyContent: mapJustifyContent(justifyContentLegacy),
		alignItems: mapAlignItems(alignItemsLegacy),
	};

	function mapAlignItems(value: string | undefined): 'start' | 'center' | 'end' | undefined {
		switch (value) {
			case 'flex-start':
				return 'start';
			case 'flex-end':
				return 'end';
			case 'center':
				return 'center';
			default:
				return undefined;
		}
	}

	function mapJustifyContent(
		value: string | undefined
	): 'start' | 'center' | 'end' | 'space-between' | 'space-around' | undefined {
		switch (value) {
			case 'flex-start':
				return 'start';
			case 'flex-end':
				return 'end';
			case 'center':
				return 'center';
			default:
				return undefined;
		}
	}
}

function normalizeVideoContent(raw: Record<string, unknown>): ModuleContent {
	return {
		videoUrl: getFirstString(raw, ['VideoUrl', 'videoUrl']) ?? '',
		videoThumbnailUrl: getFirstString(raw, ['VideoThumbnailUrl', 'videoThumbnailUrl']),
		videoId: getFirstNumber(raw, ['VideoId', 'videoId']),
		header: getFirstString(raw, ['Header', 'header']),
		descriptionHeader: getFirstString(raw, ['DescriptionHeader', 'descriptionHeader']),
		description: getFirstString(raw, ['Description', 'description']),
		showInPopup: getFirstBoolean(raw, ['ShowInPopup', 'showInPopup']),
	};
}

function normalizeIFrameContent(raw: Record<string, unknown>): ModuleContent {
	return {
		iframeSrc: getFirstString(raw, ['IframeSRC', 'iframeSrc']) ?? '',
		useQueryString: getFirstBoolean(raw, ['UseQueryString', 'useQueryString']) ?? false,
		queryStringName: getFirstString(raw, ['QueryStringName', 'queryStringName']),
	};
}

function normalizeFormContent(raw: Record<string, unknown>): ModuleContent {
	const button = asRecord(raw.Button);
	return {
		formKey: getFirstString(raw, ['FormKey', 'formKey']) ?? '',
		buttonText:
			(button ? getFirstString(button, ['Text', 'text']) : undefined) ?? getFirstString(raw, ['buttonText']) ?? 'SUBMIT',
		preSubmitText: getFirstString(raw, ['PreSubmitText', 'preSubmitText']) ?? '',
		postSubmitText: getFirstString(raw, ['PostSubmitText', 'postSubmitText']) ?? '',
	};
}

function getFirstString(raw: Record<string, unknown>, keys: string[]): string | undefined {
	for (const key of keys) {
		if (typeof raw[key] === 'string' && raw[key] !== '') {
			return raw[key] as string;
		}
	}

	return undefined;
}

function getFirstNumber(raw: Record<string, unknown>, keys: string[]): number | undefined {
	for (const key of keys) {
		if (typeof raw[key] === 'number') {
			return raw[key] as number;
		}
	}

	return undefined;
}

function getFirstBoolean(raw: Record<string, unknown>, keys: string[]): boolean | undefined {
	for (const key of keys) {
		if (typeof raw[key] === 'boolean') {
			return raw[key] as boolean;
		}
	}

	return undefined;
}
